<template>
  <form @submit="onSubmit">
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsInputField
        name="code"
        :label="t('deliveryentity.detail.code')"
        :placeholder="t('deliveryentity.detail.code')"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsInputField
        name="label"
        :label="t('deliveryentity.detail.label')"
        :placeholder="t('deliveryentity.detail.label')"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsTextAreaField
        name="description"
        :label="t('deliveryentity.detail.description')"
        :placeholder="t('deliveryentity.detail.description')"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="format"
        :label="t('deliveryentity.detail.format')"
        :placeholder="$t('deliveryentity.detail.format')"
        :options="formatTypes"
        :custom-label="formatTypeLabel"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="model"
        :label="t('deliveryentity.detail.model')"
        :placeholder="$t('deliveryentity.detail.model')"
        :options="entityModels"
        :custom-label="entityModelLabel"
        :sortFunction="entityModelSortFunction"
        :mod-loading="fetchingModels"
      />
    </vl-form-column>
    <vl-form-column v-if="entityModel && !(isCodeList || isTable)" v-vl-spacer:bottom.small >
      <PbsSelectField
        name="crs"
        :label="t('deliveryentity.detail.crs')"
        :placeholder="$t('deliveryentity.detail.crs')"
        :options="sridTypes"
        :custom-label="(e) => e.name"
        :mod-loading="fetchingModels"
      />
    </vl-form-column>
    <vl-form-column v-if="isCodeList" v-vl-spacer:bottom.small>
      <PbsSelectField
        name="dataConnection"
        :label="t('deliveryentity.detail.dataConnection')"
        :placeholder="$t('deliveryentity.detail.dataConnection')"
        :mod-loading="dataConnectionStore.loading"
        :options="dataConnections"
        :custom-label="(e) => e.code + ' (' + e.label + ')'"
      />
    </vl-form-column>
    <vl-form-column v-if="isCodeList" v-vl-spacer:bottom.small>
      <PbsInputField
        name="schema"
        :label="t('deliveryentity.detail.schema')"
        :placeholder="t('deliveryentity.detail.schema')"
      />
    </vl-form-column>
    <vl-action-group v-vl-spacer:bottom.small>
      <vl-button icon="save" :mod-loading="reloading" mod-icon-before type="submit">
        {{ $t('general.save') }}
      </vl-button>
      <vl-button icon="cross" mod-icon-before mod-secondary mod-error @click="editStore.closeCreate">
        {{ $t('general.cancel') }}
      </vl-button>
    </vl-action-group>
  </form>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { FormatTypeDto, CrsDto, SearchEntityModelDto, DataConnectionDto } from '@/api/backoffice/clients';
import { useForm } from 'vee-validate';
import { object, string, mixed } from 'yup';
import { useI18n } from 'vue-i18n';
import { useDeliveryEntityStore } from '@/modules/configuration/store/delivery-entity/delivery-entity.store';
import { useEntityModelSelectStore } from '@/modules/configuration/store/entity-model/entity-model-select.store';
import { useDeliveryProcessDefinitionEditStore } from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-edit.store';
import PbsSelectField from '@/components/forms/PbsSelectField.vue';
import PbsInputField from '@/components/forms/PbsInputField.vue';
import PbsTextAreaField from '@/components/forms/PbsTextAreaField.vue';
import { entityModelLabel, formatTypeLabel, entityModelSortFunction } from '@/modules/configuration/common';
import { RegularExpressions, noSurroundingSpaces } from '@/helpers';
import { ENUM_EntityTypes } from '@/modules/configuration/common';
import DeliveryDetail from '@/views/delivery/DeliveryDetail.vue';
import { useDataconnectionStore } from '@/modules/configuration/store/dataconnection/dataconnection.store';

const { t } = useI18n();

const store = useDeliveryEntityStore();
const entityModelSelectStore = useEntityModelSelectStore();
const editStore = useDeliveryProcessDefinitionEditStore();
const dataConnectionStore = useDataconnectionStore();

const props = defineProps({
  specificationId: {
    type: Number,
    required: true
  }
});

const validationSchema = object({
  label: string()
    .required(t('validations.messages.required', { _field_: t('deliveryentity.detail.label') }))
    .test(noSurroundingSpaces(t('deliveryentity.detail.label')))
    .max(256, t('validations.messages.maxChar', { _field_: t('deliveryentity.detail.label'), length: 256 })),
  code: string()
    .required(t('validations.messages.required', { _field_: t('deliveryentity.detail.code') }))
    .max(64, t('validations.messages.maxChar', { _field_: t('deliveryentity.detail.code'), length: 64 }))
    .matches(
      RegularExpressions.noSpaces,
      t('validations.messages.noSpaces', { _field_: t('deliveryentity.detail.code') })
    ),
  description: string().required(
    t('validations.messages.required', { _field_: t('deliveryentity.detail.description') })
  ),
  format: object().required(t('validations.messages.required', { _field_: t('deliveryentity.detail.format') })),
  model: object().required(t('validations.messages.required', { _field_: t('deliveryentity.detail.model') })),
  schema: string()
    .matches(
      RegularExpressions.noSpaces,
      t('validations.messages.noSpaces', { _field_: t('deliveryentity.detail.schema') })
    ),
  custom: mixed().test(
            'custom-validation',
            'The properties do not meet the validation criteria',
            function (_, context) {
                const { crs
                } = context.parent;
                           
                if (!crs && !(isCodeList.value || isTable.value)) {
                  return context.createError({
                    path: 'crs',
                    message: t('validations.messages.required', {
                      _field_: t('deliveryentity.detail.crs')
                    })
                  });  
                }

                return true;
            }
        )
});

const { resetForm, handleSubmit, values } = useForm({
  validationSchema
});

const formatTypes = computed((): FormatTypeDto[] => {
  return store.formatTypes ?? [];
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const sridTypes = computed((): CrsDto[] => {
  return store.sridTypes ?? [];
});

const fetchingModels = computed((): Boolean => {
  return entityModelSelectStore.loading;
});

const entityModels = computed((): SearchEntityModelDto[] => {
  return entityModelSelectStore.items ?? [];
});

const entityModel = computed(() => {
  return values.model;
});

const dataConnections = computed((): DataConnectionDto[] => {
  // For now filter on Operations dataconnection
  return (dataConnectionStore.items ?? []).filter((dc) => dc.id === 3);
});

const isTable = computed((): boolean => {
  return entityModel.value?.entityTypeName == ENUM_EntityTypes.Table.name;
});

const isCodeList = computed((): boolean => {
  return entityModel.value?.entityTypeName == ENUM_EntityTypes.CodeList.name;
});

onMounted(async () => {
  await entityModelSelectStore.getModels({ force: true, contextTypeName: 'Delivery' });
  await dataConnectionStore.get();
  resetForm();
});

const onSuccess = async (values) => {
  await store.create(props.specificationId, {
    ...values,
    deliverySpecificationId: props.specificationId,
    modelId: values.model.id,
    format: values.format.code,
    crs: values.crs ? values.crs.name : 'Lambert72', // TODO: fix default crs
    schema: values.schema === "" ? null : values.schema,
    dataConnectionId: values.dataConnection?.id
  });
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);

defineExpose({ onSubmit });
</script>
